import type {
  StoryboardDbStoreName,
  StoryboardProject,
  StoryboardProjectDraft,
  StoryboardProjectImportPayload,
  StoryboardProjectStatus,
  StoryboardSegment,
  StoryboardSegmentDraft,
  StoryboardSegmentPreviewEntry,
  StoryboardSegmentStatus,
  StoryboardSegmentTask,
  StoryboardSegmentTaskStatus,
  StoryboardTransitionConfig,
  StoryboardVersionSnapshot,
  StoryboardMediaRecord,
  StoryboardPreviewDownloadSource,
  StoryboardPreviewSourceMetadata,
} from './types';

const DB_NAME = 'iarts_storyboard_v1';
const DB_VERSION = 2;

const STORE_PROJECTS: StoryboardDbStoreName = 'projects';
const STORE_SEGMENTS: StoryboardDbStoreName = 'segments';
const STORE_VERSIONS: StoryboardDbStoreName = 'versions';
const STORE_MEDIA: StoryboardDbStoreName = 'media';

let dbPromise: Promise<IDBDatabase | null> | null = null;

const hasIndexedDbSupport = (): boolean => typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined';

const generateId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const sanitizeDownloadSource = (
  value: unknown
): StoryboardPreviewDownloadSource | undefined => {
  if (!isPlainRecord(value)) {
    return undefined;
  }
  if (value.type !== 'sora') {
    return undefined;
  }
  const videoId = isNonEmptyString(value.videoId) ? value.videoId.trim() : '';
  if (!videoId) {
    return undefined;
  }
  const fileName = isNonEmptyString(value.fileName) ? value.fileName.trim() : undefined;
  return fileName ? { type: 'sora', videoId, fileName } : { type: 'sora', videoId };
};

const parseBooleanLike = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (!normalized) {
      return undefined;
    }
    if (['true', '1', 'yes', 'y'].includes(normalized)) {
      return true;
    }
    if (['false', '0', 'no', 'n'].includes(normalized)) {
      return false;
    }
    return undefined;
  }
  if (typeof value === 'number') {
    if (Number.isNaN(value)) {
      return undefined;
    }
    if (value === 1) {
      return true;
    }
    if (value === 0) {
      return false;
    }
  }
  return undefined;
};

const sanitizeSourceMetadata = (
  value: unknown
): StoryboardPreviewSourceMetadata | undefined => {
  if (!isPlainRecord(value)) {
    return undefined;
  }
  const endpoint = isNonEmptyString(value.endpoint) ? value.endpoint.trim() : undefined;
  const modelId = isNonEmptyString(value.modelId) ? value.modelId.trim() : undefined;
  const modelKey = isNonEmptyString(value.modelKey) ? value.modelKey.trim() : undefined;
  const booleanCandidate =
    (value as { isPrivate?: unknown }).isPrivate ?? (value as { private?: unknown }).private;
  const isPrivate = parseBooleanLike(booleanCandidate);
  if (!endpoint && !modelId && !modelKey && isPrivate === undefined) {
    return undefined;
  }
  return {
    endpoint,
    modelId,
    modelKey,
    ...(isPrivate === undefined ? {} : { isPrivate }),
  };
};

const PROJECT_STATUS_VALUES: StoryboardProjectStatus[] = ['draft', 'active', 'archived'];
const SEGMENT_STATUS_VALUES: StoryboardSegmentStatus[] = ['idle', 'draft', 'queued', 'generating', 'success', 'failed'];

const promisifyRequest = <T>(request: IDBRequest<T>): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });

const openStoryboardDb = async (): Promise<IDBDatabase | null> => {
  if (!hasIndexedDbSupport()) {
    return null;
  }
  if (!dbPromise) {
    dbPromise = new Promise<IDBDatabase | null>((resolve, reject) => {
      try {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        request.onupgradeneeded = () => {
          const { result } = request;
          if (!result.objectStoreNames.contains(STORE_PROJECTS)) {
            const store = result.createObjectStore(STORE_PROJECTS, { keyPath: 'projectId' });
            store.createIndex('by_lastUpdated', 'lastUpdated', { unique: false });
          }
          if (!result.objectStoreNames.contains(STORE_SEGMENTS)) {
            const store = result.createObjectStore(STORE_SEGMENTS, { keyPath: 'segmentId' });
            store.createIndex('by_projectId', 'projectId', { unique: false });
            store.createIndex('by_projectOrder', ['projectId', 'order'], { unique: false });
          }
          if (!result.objectStoreNames.contains(STORE_VERSIONS)) {
            const store = result.createObjectStore(STORE_VERSIONS, { keyPath: 'versionId' });
            store.createIndex('by_projectId', 'projectId', { unique: false });
          }
          if (!result.objectStoreNames.contains(STORE_MEDIA)) {
            const store = result.createObjectStore(STORE_MEDIA, { keyPath: 'previewId' });
            store.createIndex('by_projectId', 'projectId', { unique: false });
            store.createIndex('by_segmentId', 'segmentId', { unique: false });
          }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error('Failed to open storyboard database'));
        request.onblocked = () => {
          reject(new Error('IndexedDB open request blocked by another connection'));
        };
      } catch (error) {
        reject(error);
      }
    });
  }
  try {
    return await dbPromise;
  } catch (error) {
    console.error('[storyboard] Failed to open database', error);
    dbPromise = null;
    return null;
  }
};

const runStoreOperation = async <K extends StoryboardDbStoreName, R>(
  storeName: K,
  mode: IDBTransactionMode,
  executor: (store: IDBObjectStore) => Promise<R>
): Promise<R | null> => {
  const db = await openStoryboardDb();
  if (!db) {
    return null;
  }
  return new Promise<R | null>((resolve, reject) => {
    try {
      const tx = db.transaction(storeName, mode);
      const store = tx.objectStore(storeName);
      let operationResult: R | undefined;
      Promise.resolve(executor(store))
        .then((result) => {
          operationResult = result;
        })
        .catch((error) => {
          reject(error);
          try {
            tx.abort();
          } catch {
            // ignore abort errors caused by a finished transaction
          }
        });
      tx.oncomplete = () => {
        resolve(operationResult ?? null);
      };
      tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
      tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
    } catch (error) {
      reject(error);
    }
  });
};

type LegacyStoryboardProject = StoryboardProject & {
  meta?: {
    goalDuration?: number;
    styleTags?: string[];
    description?: string;
  };
};

type LegacyStoryboardSegment = Omit<
  StoryboardSegment,
  'previewEntries' | 'selectedPreviewId' | 'tasks'
> & {
  previewEntries?: StoryboardSegmentPreviewEntry[];
  selectedPreviewId?: string;
  tasks?: Array<Partial<StoryboardSegmentTask>>;
};

const normalizeProjectRecord = (project: LegacyStoryboardProject): StoryboardProject => {
  if (!project) {
    throw new Error('Invalid project record');
  }
  const { projectId, createdAt, lastUpdated } = project;
  const name = (project.name ?? '').trim();
  const status = project.status ?? 'draft';
  const safeCreatedAt = typeof createdAt === 'number' ? createdAt : Date.now();
  const safeUpdatedAt = typeof lastUpdated === 'number' ? lastUpdated : safeCreatedAt;
  return {
    projectId,
    name,
    status,
    createdAt: safeCreatedAt,
    lastUpdated: safeUpdatedAt,
  };
};

const projectNameExists = async (name: string): Promise<boolean> => {
  const normalized = name.trim();
  if (!normalized) return false;
  const result = await runStoreOperation(STORE_PROJECTS, 'readonly', async (store) => {
    return new Promise<boolean>((resolve, reject) => {
      const request = store.openCursor();
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) {
          resolve(false);
          return;
        }
        const raw = cursor.value as LegacyStoryboardProject | undefined;
        if (!raw) {
          cursor.continue();
          return;
        }
        const record = normalizeProjectRecord(raw);
        if (record.name.trim() === normalized) {
          resolve(true);
          return;
        }
        cursor.continue();
      };
      request.onerror = () => reject(request.error ?? new Error('Failed to check project uniqueness'));
    });
  });
  return Boolean(result);
};

const ensureUniqueProjectName = async (baseName: string): Promise<string> => {
  const safeBase = baseName.trim() || '导入项目';
  let candidate = safeBase;
  let suffix = 1;
  while (await projectNameExists(candidate)) {
    candidate = `${safeBase} (${suffix})`;
    suffix += 1;
    if (suffix > 1000) {
      throw new Error('存在过多同名项目，请先清理部分项目后再导入');
    }
  }
  return candidate;
};

const sanitizeTransition = (transition: unknown): StoryboardTransitionConfig | undefined => {
  if (!transition || typeof transition !== 'object') return undefined;
  const maybeTransition = transition as StoryboardTransitionConfig;
  const sanitized: StoryboardTransitionConfig = {};
  if (isNonEmptyString(maybeTransition.type)) {
    sanitized.type = maybeTransition.type.trim();
  }
  if (isFiniteNumber(maybeTransition.duration)) {
    sanitized.duration = maybeTransition.duration;
  }
  if (isNonEmptyString(maybeTransition.easing)) {
    sanitized.easing = maybeTransition.easing.trim();
  }
  return Object.keys(sanitized).length ? sanitized : undefined;
};

const clampProgress = (value: unknown): number | undefined => {
  if (!isFiniteNumber(value)) {
    return undefined;
  }
  return Math.max(0, Math.min(100, Math.round(value)));
};

const normalizeTaskStatus = (status: unknown): StoryboardSegmentTaskStatus => {
  if (!isNonEmptyString(status)) {
    return 'queued';
  }
  const normalized = status.trim().toLowerCase();
  switch (normalized) {
    case 'queued':
    case 'pending':
    case 'submitted':
    case 'waiting':
      return 'queued';
    case 'processing':
    case 'running':
    case 'in_progress':
    case 'in-progress':
    case 'generating':
      return 'generating';
    case 'success':
    case 'succeeded':
    case 'completed':
    case 'complete':
      return 'success';
    case 'failed':
    case 'error':
    case 'failure':
    case 'rejected':
    case 'cancelled':
    case 'canceled':
    case 'timeout':
      return 'failed';
    default:
      return 'queued';
  }
};

const sanitizeSegmentTasks = (
  rawTasks: unknown,
  previewEntries: StoryboardSegmentPreviewEntry[],
  fallback?: {
    taskId?: string;
    status?: StoryboardSegmentStatus;
    progress?: number;
    submittedAt?: number;
    updatedAt?: number;
  }
): StoryboardSegmentTask[] => {
  const previewIdSet = new Set(previewEntries.map((entry) => entry.previewId));
  const previewByTaskId = new Map(
    previewEntries
      .filter((entry) => isNonEmptyString(entry.taskId))
      .map((entry) => [entry.taskId as string, entry.previewId])
  );

  const sanitized: StoryboardSegmentTask[] = [];

  if (Array.isArray(rawTasks)) {
    for (const candidate of rawTasks) {
      if (!candidate || typeof candidate !== 'object') {
        continue;
      }
      const record = candidate as Partial<StoryboardSegmentTask>;
      const taskId = isNonEmptyString(record.taskId) ? record.taskId.trim() : '';
      if (!taskId) {
        continue;
      }
      const status = normalizeTaskStatus(record.status);
      const submittedAt = isFiniteNumber(record.submittedAt) ? record.submittedAt : Date.now();
      const updatedAt = isFiniteNumber(record.updatedAt) ? record.updatedAt : submittedAt;
      const progress = clampProgress(record.progress);
      const failReason = isNonEmptyString(record.failReason) ? record.failReason.trim() : undefined;

      let previewId: string | undefined;
      if (isNonEmptyString(record.previewId)) {
        const trimmed = record.previewId.trim();
        previewId = previewIdSet.has(trimmed) ? trimmed : undefined;
      }
      if (!previewId) {
        const mapped = previewByTaskId.get(taskId);
        if (mapped && previewIdSet.has(mapped)) {
          previewId = mapped;
        }
      }

      sanitized.push({
        taskId,
        status,
        progress,
        submittedAt,
        updatedAt,
        failReason,
        previewId,
      });
    }
  }

  if (fallback?.taskId && !sanitized.some((item) => item.taskId === fallback.taskId)) {
    const fallbackStatus = fallback.status ?? 'queued';
    if (fallbackStatus !== 'idle' && fallbackStatus !== 'draft') {
      sanitized.push({
        taskId: fallback.taskId,
        status: normalizeTaskStatus(fallbackStatus),
        progress: clampProgress(fallback.progress),
        submittedAt: fallback.submittedAt ?? Date.now(),
        updatedAt: fallback.updatedAt ?? fallback.submittedAt ?? Date.now(),
      });
    }
  }

  sanitized.sort((a, b) => a.submittedAt - b.submittedAt);

  const unique = new Map<string, StoryboardSegmentTask>();
  for (const task of sanitized) {
    unique.set(task.taskId, task);
  }

  return Array.from(unique.values());
};

export const listProjects = async (): Promise<StoryboardProject[]> => {
  const result = await runStoreOperation(STORE_PROJECTS, 'readonly', async (store) => {
    const request = store.getAll();
    const projects = await promisifyRequest<LegacyStoryboardProject[]>(request);
    return projects
      .filter((item): item is LegacyStoryboardProject => Boolean(item))
      .map(normalizeProjectRecord)
      .sort((a, b) => b.lastUpdated - a.lastUpdated);
  });
  return result ?? [];
};

export const getProjectById = async (projectId: string): Promise<StoryboardProject | null> => {
  if (!projectId) return null;
  const result = await runStoreOperation(STORE_PROJECTS, 'readonly', async (store) => {
    const request = store.get(projectId);
    const project = await promisifyRequest<LegacyStoryboardProject | undefined>(request);
    return project ? normalizeProjectRecord(project) : undefined;
  });
  return result ?? null;
};

const enrichProject = (draft: StoryboardProjectDraft): StoryboardProject => {
  const timestamp = Date.now();
  return {
    projectId: generateId(),
    name: draft.name.trim(),
    status: 'draft',
    createdAt: timestamp,
    lastUpdated: timestamp,
  };
};

export const createProject = async (draft: StoryboardProjectDraft): Promise<StoryboardProject | null> => {
  const name = draft.name.trim();
  if (!name) {
    throw new Error('项目名称不能为空');
  }
  if (await projectNameExists(name)) {
    throw new Error('项目名称已存在');
  }
  const project = enrichProject({ name });
  await runStoreOperation(STORE_PROJECTS, 'readwrite', async (store) => {
    await promisifyRequest(store.add(project));
    return project;
  });
  return project;
};

export const updateProject = async (project: StoryboardProject): Promise<StoryboardProject | null> => {
  const nextProject: StoryboardProject = {
    ...project,
    name: project.name.trim(),
    lastUpdated: Date.now(),
  };
  await runStoreOperation(STORE_PROJECTS, 'readwrite', async (store) => {
    await promisifyRequest(store.put(nextProject));
    return nextProject;
  });
  return nextProject;
};

export const deleteProject = async (projectId: string): Promise<void> => {
  if (!projectId) return;
  await runStoreOperation(STORE_PROJECTS, 'readwrite', async (store) => {
    await promisifyRequest(store.delete(projectId));
    return undefined;
  });
  await runStoreOperation(STORE_SEGMENTS, 'readwrite', async (store) =>
    new Promise<void>((resolve, reject) => {
      const index = store.index('by_projectId');
      const range = IDBKeyRange.only(projectId);
      const request = index.openKeyCursor(range);
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) {
          resolve();
          return;
        }
        const primaryKey = cursor.primaryKey;
        if (primaryKey !== undefined && primaryKey !== null) {
          store.delete(primaryKey);
        }
        cursor.continue();
      };
      request.onerror = () => reject(request.error ?? new Error('Failed to delete project segments'));
    })
  );
};

const enrichSegment = (draft: StoryboardSegmentDraft): StoryboardSegment => {
  const timestamp = Date.now();
  return {
    segmentId: generateId(),
    projectId: draft.projectId,
    order: draft.order,
    title: draft.title,
    modelId: undefined,
    prompt: undefined,
    referenceFrame: undefined,
    remixVideoId: undefined,
    duration: undefined,
    transition: undefined,
    taskId: undefined,
    status: 'draft',
    previewUrl: undefined,
    selectedPreviewId: undefined,
    previewEntries: [],
    progress: undefined,
    tasks: [],
    createdAt: timestamp,
    lastUpdated: timestamp,
  };
};

const normalizeSegment = (segment: LegacyStoryboardSegment): StoryboardSegment => {
  const entries = Array.isArray(segment.previewEntries)
    ? segment.previewEntries.map<StoryboardSegmentPreviewEntry>((entry) => ({
        previewId: entry.previewId ?? generateId(),
        segmentId: entry.segmentId ?? segment.segmentId,
        url: entry.url,
        createdAt: entry.createdAt ?? segment.lastUpdated ?? Date.now(),
        label: entry.label,
        duration: entry.duration,
        taskId: entry.taskId,
        notes: entry.notes,
        downloadSource: sanitizeDownloadSource(entry.downloadSource),
        sourceMetadata: sanitizeSourceMetadata((entry as { sourceMetadata?: unknown }).sourceMetadata),
      }))
    : [];

  const fallbackTaskId = isNonEmptyString(segment.taskId) ? segment.taskId.trim() : undefined;
  const tasks = sanitizeSegmentTasks(
    (segment as { tasks?: unknown }).tasks,
    entries,
    fallbackTaskId
      ? {
          taskId: fallbackTaskId,
          status: segment.status,
          progress: segment.progress,
          submittedAt: segment.createdAt,
          updatedAt: segment.lastUpdated,
        }
      : undefined
  );

  let selectedPreviewId = segment.selectedPreviewId;
  let previewUrl = segment.previewUrl;

  if (!entries.length && previewUrl) {
    const fallbackId = `legacy-${segment.segmentId}`;
    entries.push({
      previewId: fallbackId,
      segmentId: segment.segmentId,
      url: previewUrl,
      createdAt: segment.lastUpdated ?? Date.now(),
    });
    selectedPreviewId = selectedPreviewId ?? fallbackId;
  }

  if (!selectedPreviewId && entries.length) {
    selectedPreviewId = entries[0].previewId;
  }

  if (!previewUrl && selectedPreviewId) {
    const selectedEntry = entries.find((entry) => entry.previewId === selectedPreviewId);
    previewUrl = selectedEntry?.url;
  }

  return {
    ...segment,
    previewEntries: entries,
    selectedPreviewId,
    previewUrl,
    progress: clampProgress(segment.progress),
    tasks,
  };
};

export interface StoryboardMediaRecordInput {
  previewId: string;
  projectId: string;
  segmentId: string;
  blob: Blob;
  mimeType?: string;
  originalUrl?: string;
  fileName?: string;
  createdAt?: number;
}

export interface StoryboardMediaImportAsset {
  sourcePreviewId: string;
  blob: Blob;
  mimeType?: string;
  fileName?: string;
  originalUrl?: string;
}

const hydrateMediaRecord = (
  input: StoryboardMediaRecordInput,
  existing?: StoryboardMediaRecord | null
): StoryboardMediaRecord => {
  const now = Date.now();
  const baseCreatedAt = isFiniteNumber(input.createdAt)
    ? input.createdAt
    : existing?.createdAt ?? now;
  const mimeType = isNonEmptyString(input.mimeType)
    ? input.mimeType
    : existing?.mimeType ?? (input.blob.type || 'application/octet-stream');
  const originalUrl = isNonEmptyString(input.originalUrl)
    ? input.originalUrl.trim()
    : existing?.originalUrl;
  const fileName = isNonEmptyString(input.fileName) ? input.fileName.trim() : existing?.fileName;
  return {
    previewId: input.previewId,
    projectId: input.projectId,
    segmentId: input.segmentId,
    blob: input.blob,
    mimeType,
    size: input.blob.size,
    createdAt: baseCreatedAt,
    updatedAt: now,
    originalUrl,
    fileName,
  };
};

const buildImportedSegment = (
  rawSegment: Partial<StoryboardSegment>,
  order: number,
  projectId: string,
  defaultTimestamp: number
): { segment: StoryboardSegment; previewIdMap: Map<string | undefined, string> } => {
  const segmentId = generateId();
  const title = isNonEmptyString(rawSegment.title) ? rawSegment.title.trim() : `单元 ${order + 1}`;
  const createdAt = isFiniteNumber(rawSegment.createdAt) ? rawSegment.createdAt : defaultTimestamp;
  const lastUpdated = isFiniteNumber(rawSegment.lastUpdated) ? rawSegment.lastUpdated : createdAt;
  const status = SEGMENT_STATUS_VALUES.includes(rawSegment.status as StoryboardSegmentStatus)
    ? (rawSegment.status as StoryboardSegmentStatus)
    : 'draft';

  const previewEntriesRaw = Array.isArray(rawSegment.previewEntries) ? rawSegment.previewEntries : [];
  const previewIdMap = new Map<string | undefined, string>();
  const previewEntries: StoryboardSegmentPreviewEntry[] = [];
  for (const entry of previewEntriesRaw) {
    if (!entry || typeof entry !== 'object') continue;
    const record = entry as Partial<StoryboardSegmentPreviewEntry>;
    const downloadSource = sanitizeDownloadSource((record as { downloadSource?: unknown }).downloadSource);
    const sourceMetadata = sanitizeSourceMetadata((record as { sourceMetadata?: unknown }).sourceMetadata);
    const normalizedUrl = isNonEmptyString(record.url) ? record.url.trim() : undefined;
    if (!normalizedUrl && !downloadSource) continue;
    const newPreviewId = generateId();
    previewIdMap.set(record.previewId, newPreviewId);
    previewEntries.push({
      previewId: newPreviewId,
      segmentId,
      url: normalizedUrl,
      createdAt: isFiniteNumber(record.createdAt) ? record.createdAt : lastUpdated,
      label: isNonEmptyString(record.label) ? record.label.trim() : undefined,
      duration: isFiniteNumber(record.duration) ? record.duration : undefined,
      taskId: undefined,
      notes: isNonEmptyString(record.notes) ? record.notes.trim() : undefined,
      downloadSource,
      sourceMetadata,
    });
  }

  let selectedPreviewId: string | undefined;
  if (isNonEmptyString(rawSegment.selectedPreviewId)) {
    selectedPreviewId = previewIdMap.get(rawSegment.selectedPreviewId);
  }
  if (!selectedPreviewId && previewEntries.length) {
    selectedPreviewId = previewEntries[0].previewId;
  }
  const previewUrlCandidate = isNonEmptyString(rawSegment.previewUrl) ? rawSegment.previewUrl.trim() : undefined;
  const previewUrl =
    previewUrlCandidate ??
    (selectedPreviewId ? previewEntries.find((entry) => entry.previewId === selectedPreviewId)?.url : undefined);

  const fallbackTaskId = isNonEmptyString(rawSegment.taskId) ? rawSegment.taskId.trim() : undefined;
  const tasks = sanitizeSegmentTasks(
    (rawSegment as { tasks?: unknown }).tasks,
    previewEntries,
    fallbackTaskId
      ? {
          taskId: fallbackTaskId,
          status: rawSegment.status,
          progress: rawSegment.progress,
          submittedAt: createdAt,
          updatedAt: lastUpdated,
        }
      : undefined
  );
  const activeTask =
    [...tasks]
      .reverse()
      .find((task) => task.status === 'queued' || task.status === 'generating') ?? null;

  const segment = {
    segmentId,
    projectId,
    order,
    title,
    modelId: isNonEmptyString(rawSegment.modelId) ? rawSegment.modelId : undefined,
    prompt: isNonEmptyString(rawSegment.prompt) ? rawSegment.prompt : undefined,
    referenceFrame: isNonEmptyString(rawSegment.referenceFrame) ? rawSegment.referenceFrame : undefined,
    duration: isFiniteNumber(rawSegment.duration) ? rawSegment.duration : undefined,
    transition: sanitizeTransition(rawSegment.transition),
    taskId: activeTask?.taskId,
    status,
    previewUrl,
    selectedPreviewId,
    previewEntries,
    progress: clampProgress(rawSegment.progress),
    tasks,
    createdAt,
    lastUpdated,
  };

  return { segment, previewIdMap };
};

export const getMediaRecord = async (previewId: string): Promise<StoryboardMediaRecord | null> => {
  if (!previewId) return null;
  const result = await runStoreOperation(STORE_MEDIA, 'readonly', async (store) => {
    const request = store.get(previewId);
    const record = await promisifyRequest<StoryboardMediaRecord | undefined>(request);
    return record ?? null;
  });
  return result ?? null;
};

export const saveMediaRecord = async (
  input: StoryboardMediaRecordInput
): Promise<StoryboardMediaRecord | null> => {
  if (!input?.previewId || !input.projectId || !input.segmentId) {
    throw new Error('Invalid media record input');
  }
  const existing = await getMediaRecord(input.previewId);
  const record = hydrateMediaRecord(input, existing ?? undefined);
  await runStoreOperation(STORE_MEDIA, 'readwrite', async (store) => {
    await promisifyRequest(store.put(record));
    return record;
  });
  return record;
};

export const deleteMediaRecord = async (previewId: string): Promise<void> => {
  if (!previewId) return;
  await runStoreOperation(STORE_MEDIA, 'readwrite', async (store) => {
    await promisifyRequest(store.delete(previewId));
    return undefined;
  });
};

export const listMediaRecordsByProject = async (
  projectId: string
): Promise<StoryboardMediaRecord[]> => {
  if (!projectId) return [];
  const result = await runStoreOperation(STORE_MEDIA, 'readonly', async (store) => {
    const index = store.index('by_projectId');
    const request = index.getAll(IDBKeyRange.only(projectId));
    const records = await promisifyRequest<StoryboardMediaRecord[]>(request);
    return records ?? [];
  });
  return result ?? [];
};

export const listMediaRecordsBySegment = async (
  segmentId: string
): Promise<StoryboardMediaRecord[]> => {
  if (!segmentId) return [];
  const result = await runStoreOperation(STORE_MEDIA, 'readonly', async (store) => {
    const index = store.index('by_segmentId');
    const request = index.getAll(IDBKeyRange.only(segmentId));
    const records = await promisifyRequest<StoryboardMediaRecord[]>(request);
    return records ?? [];
  });
  return result ?? [];
};

export const bulkImportMediaRecords = async (
  inputs: StoryboardMediaRecordInput[]
): Promise<void> => {
  if (!inputs.length) return;
  await runStoreOperation(STORE_MEDIA, 'readwrite', async (store) => {
    for (const input of inputs) {
      const existingRequest = store.get(input.previewId);
      const existing = await promisifyRequest<StoryboardMediaRecord | undefined>(existingRequest);
      const record = hydrateMediaRecord(input, existing ?? undefined);
      await promisifyRequest(store.put(record));
    }
    return undefined;
  });
};

export const saveImportedMediaAssets = async (
  projectId: string,
  mapping: Record<string, { previewId: string; segmentId: string }>,
  assets: StoryboardMediaImportAsset[]
): Promise<void> => {
  if (!projectId || !assets.length) return;
  const inputs: StoryboardMediaRecordInput[] = [];
  for (const asset of assets) {
    if (!asset || !asset.sourcePreviewId) continue;
    const target = mapping[asset.sourcePreviewId];
    if (!target) continue;
    inputs.push({
      previewId: target.previewId,
      projectId,
      segmentId: target.segmentId,
      blob: asset.blob,
      mimeType: asset.mimeType,
      originalUrl: asset.originalUrl,
      fileName: asset.fileName,
    });
  }
  if (!inputs.length) return;
  await bulkImportMediaRecords(inputs);
};

export const importProject = async (
  payload: StoryboardProjectImportPayload
): Promise<{
  project: StoryboardProject;
  segments: StoryboardSegment[];
  previewIdMap: Record<string, { previewId: string; segmentId: string }>;
} | null> => {
  if (!payload || typeof payload !== 'object') {
    throw new Error('故事板文件格式无效');
  }
  if (!payload.project || typeof payload.project !== 'object') {
    throw new Error('故事板文件缺少项目信息');
  }

  const now = Date.now();
  const baseName = isNonEmptyString(payload.project.name) ? payload.project.name.trim() : '导入项目';

  const projectName = await ensureUniqueProjectName(baseName);
  const status = PROJECT_STATUS_VALUES.includes(payload.project.status as StoryboardProjectStatus)
    ? (payload.project.status as StoryboardProjectStatus)
    : 'draft';
  const createdAt = isFiniteNumber(payload.project.createdAt) ? payload.project.createdAt : now;
  const projectId = generateId();

  const rawSegments = Array.isArray(payload.segments)
    ? payload.segments.filter((item): item is Partial<StoryboardSegment> => Boolean(item))
    : [];
  const builtSegments = rawSegments
    .map((segment, index) => ({
      segment,
      order: isFiniteNumber(segment.order) ? segment.order : index,
    }))
    .sort((a, b) => a.order - b.order)
    .map((entry, index) => buildImportedSegment(entry.segment, index, projectId, now));
  const sortedSegments = builtSegments.map((item) => item.segment);

  const latestTimestamp = sortedSegments.reduce((acc, segment) => Math.max(acc, segment.lastUpdated), now);
  const project: StoryboardProject = {
    projectId,
    name: projectName,
    status,
    createdAt,
    lastUpdated: latestTimestamp,
  };

  try {
    await runStoreOperation(STORE_PROJECTS, 'readwrite', async (store) => {
      await promisifyRequest(store.add(project));
      return undefined;
    });
  } catch (error) {
    console.error('[storyboard] 保存导入项目失败', error);
    throw new Error('保存导入项目失败，请稍后重试');
  }

  try {
    if (sortedSegments.length) {
      await runStoreOperation(STORE_SEGMENTS, 'readwrite', async (store) => {
        for (const segment of sortedSegments) {
          await promisifyRequest(store.add(segment));
        }
        return undefined;
      });
    }
  } catch (error) {
    console.error('[storyboard] 导入项目时写入片段失败，尝试回滚', error);
    try {
      await runStoreOperation(STORE_PROJECTS, 'readwrite', async (store) => {
        await promisifyRequest(store.delete(projectId));
        return undefined;
      });
    } catch (cleanupError) {
      console.error('[storyboard] 导入项目失败后清理残留项目失败', cleanupError);
    }
    throw new Error('导入项目片段失败，请确认文件内容后重试');
  }

  const previewIdMap: Record<string, { previewId: string; segmentId: string }> = {};
  for (const item of builtSegments) {
    for (const [sourceId, targetId] of item.previewIdMap.entries()) {
      if (!sourceId || !targetId) continue;
      previewIdMap[sourceId] = { previewId: targetId, segmentId: item.segment.segmentId };
    }
  }

  return { project, segments: sortedSegments, previewIdMap };
};

export const createSegment = async (
  draft: StoryboardSegmentDraft,
  overrides?: Partial<StoryboardSegment>
): Promise<StoryboardSegment | null> => {
  const base = enrichSegment(draft);
  const segment: StoryboardSegment = { ...base, ...overrides, lastUpdated: Date.now() };
  await runStoreOperation(STORE_SEGMENTS, 'readwrite', async (store) => {
    await promisifyRequest(store.add(segment));
    return segment;
  });
  await updateProjectTimestamp(segment.projectId);
  return segment;
};

export const updateSegment = async (segment: StoryboardSegment): Promise<StoryboardSegment | null> => {
  const nextSegment: StoryboardSegment = { ...segment, lastUpdated: Date.now() };
  await runStoreOperation(STORE_SEGMENTS, 'readwrite', async (store) => {
    await promisifyRequest(store.put(nextSegment));
    return nextSegment;
  });
  await updateProjectTimestamp(segment.projectId);
  return nextSegment;
};

const updateProjectTimestamp = async (projectId: string): Promise<void> => {
  const project = await getProjectById(projectId);
  if (!project) return;
  await updateProject(project);
};

export const listSegmentsByProject = async (projectId: string): Promise<StoryboardSegment[]> => {
  if (!projectId) return [];
  const result = await runStoreOperation(STORE_SEGMENTS, 'readonly', async (store) => {
    const index = store.index('by_projectId');
    const request = index.getAll(IDBKeyRange.only(projectId));
    const segments = await promisifyRequest<StoryboardSegment[]>(request);
    return segments
      .map((segment) => normalizeSegment(segment))
      .sort((a, b) => a.order - b.order);
  });
  return result ?? [];
};

export const deleteSegment = async (segmentId: string): Promise<void> => {
  if (!segmentId) return;
  const segment = await runStoreOperation(STORE_SEGMENTS, 'readonly', async (store) => {
    const request = store.get(segmentId);
    return promisifyRequest<StoryboardSegment | undefined>(request);
  });
  if (!segment) return;
  await runStoreOperation(STORE_SEGMENTS, 'readwrite', async (store) => {
    await promisifyRequest(store.delete(segmentId));
    return undefined;
  });
  await updateProjectTimestamp(segment.projectId);
};

export const saveVersionSnapshot = async (snapshot: StoryboardVersionSnapshot): Promise<void> => {
  await runStoreOperation(STORE_VERSIONS, 'readwrite', async (store) => {
    await promisifyRequest(store.put(snapshot));
    return undefined;
  });
};

export const listVersionSnapshots = async (projectId: string): Promise<StoryboardVersionSnapshot[]> => {
  const result = await runStoreOperation(STORE_VERSIONS, 'readonly', async (store) => {
    const index = store.index('by_projectId');
    const request = index.getAll(IDBKeyRange.only(projectId));
    const versions = await promisifyRequest<StoryboardVersionSnapshot[]>(request);
    return versions.sort((a, b) => b.createdAt - a.createdAt);
  });
  return result ?? [];
};

export const resetStoryboardDatabase = async (): Promise<void> => {
  if (!hasIndexedDbSupport()) return;
  if (dbPromise) {
    const db = await dbPromise;
    db?.close();
    dbPromise = null;
  }
  await new Promise<void>((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DB_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error ?? new Error('Failed to reset storyboard database'));
    request.onblocked = () => reject(new Error('Storyboard database deletion blocked'));
  });
};
